import { analytics } from "./firebase";
import { fareParamsOf, FareProductRef } from "./fareParams";

export type RegisterMethod = "email" | "google" | "apple" | "uae_pass";
export type UserStatus = "guest" | "logged_in";

/** GA4 rejects null params; strip null/undefined but keep "" and 0. */
const clean = (p: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(Object.entries(p).filter(([, v]) => v != null));

interface SearchTripArgs {
  origin: string;
  destination: string;
  departureDate: string;
  returnDate?: string;
  passengerType: string;
  passengerCount: number;
}
interface TicketContinueArgs {
  origin: string;
  destination: string;
  travelDate: string;
  travelTime?: string;
  returnDate?: string;
  returnTime?: string;
  passengerType: string;
  trainClass: string;
  /** Selected product code (CMS, PRV, ...). */
  fare: string;
  /** Selected offer product, for train_class / fare_type. */
  fareProduct?: FareProductRef;
  passengerCount: number;
  price: number;
  cartValue: number;
  currency?: string;
}
interface CheckoutPayArgs {
  paymentMethod: string;
  price: number;
  currency?: string;
  origin: string;
  destination: string;
  travelDate: string;
  travelTime?: string;
  returnDate?: string;
  returnTime?: string;
  trainNumber?: string;
  trainClass?: string;
  ticketOption?: string;
  /** Booked fare product, for train_class / fare_type. */
  fareProduct?: FareProductRef;
  passengerCount: number;
}
interface PaymentConfirmationArgs extends CheckoutPayArgs {
  bookingReference: string;
  passengerType?: string;
}
interface ChangeDateArgs {
  bookingReference: string;
  changeFees: number;
  price: number;
  currency?: string;
}
interface RoundTripArgs {
  bookingReference: string;
  newBookingReference: string;
  price: number;
  currency?: string;
  origin: string;
  destination: string;
  travelDate: string;
  fare?: string;
}

export const Analytics = {
  setUser(hashedUserId: string | null) {
    const g = analytics();
    if (!g || !hashedUserId) return;
    g("set", { user_id: hashedUserId });
  },

  /** Params carried by every later event (user_status, language). */
  setContext(o: { userStatus: UserStatus; language: string }) {
    const g = analytics();
    if (!g) return;
    g("set", { user_status: o.userStatus, language: o.language });
  },

  // send_page_view is off in initAnalytics, so App Router navigations are
  // reported here. Gives every page (home included) an event that carries
  // the setContext params.
  pageView(path: string) {
    const g = analytics();
    if (!g) return;
    g("event", "page_view", clean({
      page_path: path,
      // Origin + path only: after the Keycloak redirect the query/fragment
      // carries code, state and session_state, which must not reach GA.
      page_location: typeof window !== "undefined" ? window.location.origin + window.location.pathname : undefined,
      page_title: typeof document !== "undefined" ? document.title : undefined,
    }));
  },

  // #1
  registerEmailAttempt() {
    const g = analytics();
    if (g) g("event", "keyclock_register_email");
  },

  // #2 (+ #3 when uae_pass). hashedEmail: SHA-256 hex of the normalised
  // email, never the raw address.
  registerSuccess(method: RegisterMethod, hashedEmail?: string) {
    const g = analytics();
    if (!g) return;
    g("event", "keyclock_succs_register", clean({ register_method: method, email: hashedEmail || undefined }));
    if (method === "uae_pass") {
      g("event", "keyclock_succs_register_uaepass", { register_method: "uae_pass" });
    }
  },

  // #4
  searchTrip(o: SearchTripArgs) {
    const g = analytics();
    if (!g) return;
    g("event", "search_btn", clean({
      origin: o.origin,
      destination: o.destination,
      departure_date: o.departureDate,
      return_date: o.returnDate ?? "",
      passenger_type: o.passengerType,
      passenger_count: o.passengerCount,
    }));
  },

  // #5
  ticketOptionContinue(o: TicketContinueArgs) {
    const g = analytics();
    if (!g) return;
    g("event", "ticket_opt_continue_btn", clean({
      origin: o.origin,
      destination: o.destination,
      travel_date: o.travelDate,
      travel_time: o.travelTime ?? "",
      return_date: o.returnDate ?? "",
      return_time: o.returnTime ?? "",
      passenger_type: o.passengerType,
      ...fareParamsOf(o.fareProduct, o.trainClass),
      fare: o.fare,
      passenger_count: o.passengerCount,
      price: o.price,
      cart_value: o.cartValue,
      currency: o.currency ?? "AED",
    }));
  },

  // #6
  checkoutPay(o: CheckoutPayArgs) {
    const g = analytics();
    if (!g) return;
    g("event", "checkout_pay_btn", clean({
      payment_method: o.paymentMethod,
      price: o.price,
      currency: o.currency ?? "AED",
      origin: o.origin,
      destination: o.destination,
      travel_date: o.travelDate,
      travel_time: o.travelTime ?? "",
      return_date: o.returnDate ?? "",
      return_time: o.returnTime ?? "",
      train_number: o.trainNumber ?? "",
      ...fareParamsOf(o.fareProduct, o.trainClass),
      ticket_option: o.ticketOption ?? "",
      passenger_count: o.passengerCount,
    }));
  },

  // #7
  paymentConfirmation(o: PaymentConfirmationArgs) {
    const g = analytics();
    if (!g) return;
    g("event", "payment_confirmation", clean({
      booking_reference: o.bookingReference,
      payment_method: o.paymentMethod,
      price: o.price,
      currency: o.currency ?? "AED",
      origin: o.origin,
      destination: o.destination,
      travel_date: o.travelDate,
      travel_time: o.travelTime ?? "",
      return_date: o.returnDate ?? "",
      return_time: o.returnTime ?? "",
      train_number: o.trainNumber ?? "",
      ...fareParamsOf(o.fareProduct, o.trainClass),
      ticket_option: o.ticketOption ?? "",
      passenger_type: o.passengerType ?? "",
      passenger_count: o.passengerCount,
      // Whole basket paid; equals price today (no separate add-on total).
      cart_value: o.price,
    }));
  },

  // #8
  changeDateConfirmation(o: ChangeDateArgs) {
    const g = analytics();
    if (!g) return;
    g("event", "change_payment_confirmation", clean({
      booking_reference: o.bookingReference,
      change_fees: o.changeFees,
      price: o.price,
      currency: o.currency ?? "AED",
    }));
  },

  // #9
  roundTripPaymentConfirmation(o: RoundTripArgs) {
    const g = analytics();
    if (!g) return;
    g("event", "book_roundtrip_payment_confirmation", clean({
      booking_reference: o.bookingReference,
      new_booking_reference: o.newBookingReference,
      price: o.price,
      currency: o.currency ?? "AED",
      origin: o.origin,
      destination: o.destination,
      travel_date: o.travelDate,
      fare: o.fare ?? "",
    }));
  },
};
